import { DungeonEntranceError, } from './entrance-types.js';
import { validateDungeonEnemyIconRules } from './entrance-validate.js';
function matchingRule(enemy, rules) {
    const fallback = rules.find((rule) => rule.fallback);
    const scored = rules
        .filter((rule) => !rule.fallback)
        .map((rule) => ({
        rule,
        score: rule.tags.filter((tag) => enemy.tags.includes(tag)).length * 3 +
            rule.roles.filter((role) => enemy.roles.includes(role)).length * 2,
    }))
        .filter((entry) => entry.score > 0)
        .sort((left, right) => right.score - left.score ||
        right.rule.priority - left.rule.priority ||
        left.rule.id.localeCompare(right.rule.id));
    return scored[0]?.rule ?? fallback;
}
/** Derives a tactical, three-slot public preview from the actual generated encounter roster. */
export function deriveDungeonEnemyPreview(plan, catalog, iconRules) {
    const ruleProblems = validateDungeonEnemyIconRules(iconRules);
    if (ruleProblems.length > 0)
        throw new DungeonEntranceError(ruleProblems);
    const enemyById = new Map(catalog.enemies.map((enemy) => [enemy.id, enemy]));
    const buckets = new Map();
    let totalEnemyCount = 0;
    const missingEnemyIds = new Set();
    for (const room of plan.rooms) {
        if (!room.encounter)
            continue;
        for (const member of room.encounter.members) {
            const enemy = enemyById.get(member.enemyId);
            if (!enemy) {
                missingEnemyIds.add(member.enemyId);
                continue;
            }
            const rule = matchingRule(enemy, iconRules);
            const bucket = buckets.get(rule.id) ?? { rule, enemyCount: 0, containsBoss: false };
            bucket.enemyCount += member.count;
            bucket.containsBoss ||= room.encounter.kind === 'boss' || enemy.boss === true;
            buckets.set(rule.id, bucket);
            totalEnemyCount += member.count;
        }
    }
    if (missingEnemyIds.size > 0) {
        throw new DungeonEntranceError([...missingEnemyIds].sort().map((enemyId) => ({
            code: 'preview-unknown-enemy',
            path: 'plan.rooms.encounter.members',
            message: `unknown enemy '${enemyId}'`,
        })));
    }
    if (totalEnemyCount === 0) {
        throw new DungeonEntranceError([
            {
                code: 'preview-empty-roster',
                path: 'plan.rooms',
                message: 'generated plan contains no previewable encounter members',
            },
        ]);
    }
    const ordered = [...buckets.values()].sort((left, right) => right.enemyCount - left.enemyCount ||
        Number(right.containsBoss) - Number(left.containsBoss) ||
        right.rule.priority - left.rule.priority ||
        left.rule.id.localeCompare(right.rule.id));
    const selected = ordered.slice(0, 3);
    const bossBucket = ordered.find((bucket) => bucket.containsBoss);
    if (bossBucket && !selected.includes(bossBucket)) {
        if (selected.length < 3)
            selected.push(bossBucket);
        else
            selected[2] = bossBucket;
    }
    selected.sort((left, right) => right.enemyCount - left.enemyCount ||
        Number(right.containsBoss) - Number(left.containsBoss) ||
        left.rule.id.localeCompare(right.rule.id));
    const slots = selected.map((bucket, index) => ({
        slot: index + 1,
        iconId: bucket.rule.id,
        label: bucket.rule.label,
        symbolKey: bucket.rule.symbolKey,
        preparationHint: bucket.rule.preparationHint,
        enemyCount: bucket.enemyCount,
        share: Number((bucket.enemyCount / totalEnemyCount).toFixed(4)),
        containsBoss: bucket.containsBoss,
    }));
    const bossPresence = slots.some((slot) => slot.containsBoss);
    const familyText = slots.map((slot) => slot.label.toLowerCase()).join(', ');
    return {
        planFingerprint: plan.fingerprint,
        difficultyId: plan.difficultyId,
        themeId: plan.themeId,
        slots,
        bossPresence,
        totalEnemyCount,
        accessibleLabel: `${familyText}; ${bossPresence ? 'boss presence confirmed' : 'no boss signal'}.`,
    };
}
//# sourceMappingURL=entrance-preview.js.map